import * as Tone from 'tone';

// RoutingManager constructs per-track audio graphs (effect rack, pan, volume)
export class RoutingManager {
    constructor() {
        this.masterGain = null;
        this.tracks = new Map();
    }

    initialize(masterGain) {
        this.masterGain = masterGain;
    }

    ensureTrackGraph(trackId) {
        if (this.tracks.has(trackId)) return this.tracks.get(trackId);

        const inputBus = new Tone.Gain(1);

        // Effects rack (simple but musical defaults)
        const eq = new Tone.EQ3({ low: 0, mid: 0, high: 0 });
        const compressor = new Tone.Compressor({ threshold: -24, ratio: 4, attack: 0.003, release: 0.25 });
        const chorus = new Tone.Chorus(4, 2.5, 0.2).start();
        const delay = new Tone.FeedbackDelay({ delayTime: 0.25, feedback: 0.2, wet: 0 });
        const reverb = new Tone.Reverb({ decay: 1.5, preDelay: 0.01, wet: 0.2 });

        // Create stereo panner (instead of simple Panner)
        const stereoPanner = new Tone.PanVol(0, 0); // pan: 0 = center, vol: 0dB
        const volume = new Tone.Gain(0.8);

        // Connect: input → EQ → Comp → Chorus → Delay → Reverb → StereoPanner → Volume → Master
        inputBus.chain(eq, compressor, chorus, delay, reverb, stereoPanner, volume, this.masterGain);

        const graph = { inputBus, eq, compressor, chorus, delay, reverb, stereoPanner, volume };
        this.tracks.set(trackId, graph);
        return graph;
    }

    getTrackInput(trackId) {
        return this.ensureTrackGraph(trackId).inputBus;
    }

    updateTrackParams(trackId, params = {}) {
        const graph = this.ensureTrackGraph(trackId);
        if (params.volume !== undefined) {
            graph.volume.gain.rampTo(params.volume, 0.1);
        }
        if (params.pan !== undefined) {
            // Set pan value: -1 (left) to 1 (right)
            graph.stereoPanner.pan.rampTo(params.pan, 0.1);
        }
    }

    updateTrackEffects(trackId, effects = {}) {
        const graph = this.ensureTrackGraph(trackId);

        if (effects.eq) {
            const { low, mid, high } = effects.eq;
            if (low !== undefined) graph.eq.low.value = low;
            if (mid !== undefined) graph.eq.mid.value = mid;
            if (high !== undefined) graph.eq.high.value = high;
        }

        if (effects.compressor) {
            const { threshold, ratio, attack, release } = effects.compressor;
            if (threshold !== undefined) graph.compressor.threshold.rampTo(threshold, 0.05);
            if (ratio !== undefined) graph.compressor.ratio = ratio;
            if (attack !== undefined) graph.compressor.attack = attack;
            if (release !== undefined) graph.compressor.release = release;
        }

        if (effects.chorus !== undefined) {
            // effects.chorus expected 0..1 as wet amount
            graph.chorus.wet.rampTo(Math.max(0, Math.min(1, effects.chorus)), 0.1);
        }

        if (effects.delay !== undefined) {
            // effects.delay expected 0..1 as wet amount (keep fixed time/feedback for now)
            graph.delay.wet.rampTo(Math.max(0, Math.min(1, effects.delay)), 0.1);
        }

        if (effects.reverb !== undefined) {
            graph.reverb.wet.rampTo(Math.max(0, Math.min(1, effects.reverb)), 0.1);
        }
    }

    disposeTrack(trackId) {
        const graph = this.tracks.get(trackId);
        if (!graph) return;
        try {
            Object.values(graph).forEach(node => {
                if (node && node.dispose) node.dispose();
            });
        } catch (_) { }
        this.tracks.delete(trackId);
    }

    disposeAll() {
        Array.from(this.tracks.keys()).forEach(id => this.disposeTrack(id));
        this.tracks.clear();
    }
}


